import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileText, ExternalLink, FileDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { FormValues } from "@/components/FormFields";

export const ProjectFormsCard = ({ projectId, customerId }) => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [formDef, setFormDef] = useState(null);

  useEffect(() => {
    if (!viewing) { setFormDef(null); return; }
    axios.get(`${API}/forms/${viewing.form_id}`).then(r => setFormDef(r.data)).catch(() => setFormDef(null));
  }, [viewing]);

  useEffect(() => {
    axios.get(`${API}/forms/project-submissions/${projectId}`)
      .then(res => setSubmissions(res.data || []))
      .catch(() => setSubmissions([]));
  }, [projectId]);

  return (
    <Card data-testid="project-forms-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="w-5 h-5 text-indigo-600" /> Forms ({submissions.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {submissions.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-3">No forms attached. Attach forms from the customer profile.</p>
        ) : (
          <div className="space-y-2">
            {submissions.map(sub => (
              <button key={sub.submission_id} onClick={() => setViewing(sub)} data-testid={`project-form-${sub.submission_id}`}
                className="w-full text-left flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                <div>
                  <p className="font-medium text-sm">{sub.form_name}</p>
                  <p className="text-xs text-slate-500">{new Date(sub.created_at).toLocaleDateString()} by {sub.submitted_by_name}{sub.revision > 1 ? ` · Rev ${sub.revision}` : ""}</p>
                </div>
                <Badge variant="outline" className="text-xs capitalize">{sub.status}</Badge>
              </button>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={!!viewing} onOpenChange={(v) => { if (!v) setViewing(null); }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle>{viewing.form_name}</DialogTitle>
                <p className="text-xs text-slate-500">Submitted {new Date(viewing.created_at).toLocaleString()} by {viewing.submitted_by_name}</p>
              </DialogHeader>
              <div className="py-2">
                <FormValues fields={formDef?.fields || []} data={viewing.data || {}} />
              </div>
              <DialogFooter className="flex-wrap gap-2 sm:gap-2">
                <Button variant="outline" onClick={() => window.open(`${API}/forms/submissions/${viewing.submission_id}/pdf`, "_blank")} data-testid="project-form-pdf-btn"><FileDown className="w-4 h-4 mr-2" /> PDF</Button>
                <Button variant="outline" onClick={() => setViewing(null)}>Close</Button>
                {customerId && (
                  <Button onClick={() => navigate(`/customers/${customerId}`)} data-testid="edit-in-profile-btn">
                    <ExternalLink className="w-4 h-4 mr-2" /> Edit in Customer Profile
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};
